/**
 * D252: an ORDER — a clause said to somebody, about what THEY should do.
 *
 * @remarks
 * Three shapes, read before the clause is read as the player's own act:
 *
 * - **Led**: `tell TALLY to follow me` — an order leader (the vocabulary's
 *   `orderLeaders`: `tell`, `order`, `ask` …), the body, `to`, the clause.
 * - **Named**: `TALLY follow me` — the body, then a clause opening with a
 *   verb (or a direction: `TALLY north`).
 * - **Called**: `TALLY, follow me` — the body alone, and the NEXT clause
 *   opening with a verb. Punctuation split the two, so the reading spans two
 *   segments.
 *
 * The body is resolved against the entries the consumer marks addressable
 * and nothing else, so the parser never learns what a body is, and a catalog
 * that marks none never reads an order. The clause is parsed by the full
 * clause walk, unchanged: the order is that command with its addressee
 * attached. A led order whose clause fails says why that clause failed (the
 * player plainly gave an order); a named or called reading that does not
 * parse is not an order at all, and the clause is read as it always was.
 *
 * @packageDocumentation
 */

import type { ParseContext } from '../parse-context.js';
import type { ClauseParseResult, ClauseSegment } from '../rescue/parse-guided-rescues.js';
import type { EntityRef, Token } from '../types.js';
import type { PhraseToken } from './match-clause-structure.js';

import { resolveEntityPhrase } from '../resolve/resolve-entity-phrase.js';
import { canonicalizeTokens } from './canonicalize-tokens.js';

/** The word between a led order's body and its clause. */
const ORDER_PREPOSITION = 'to';

/** How an order reading ended, and how many of the input's segments it read (two for a called order). */
export interface OrderReading {
	readonly result: ClauseParseResult;
	readonly segmentsRead: 1 | 2;
}

/** The orchestrator's clause walk, injected so the ordered clause is read exactly as any clause is. */
export type ParseOrderedClause = (clauseSegment: ClauseSegment) => ClauseParseResult;

interface Addressee {
	readonly entityRef: EntityRef;
	readonly surface: string;
}

interface OrderSplit {
	readonly addresseeTokens: readonly Token[];
	readonly isLed: boolean;
	readonly orderedTokens: readonly Token[];
}

/**
 * The body these tokens name, when they are a noun phrase and name exactly one entry the consumer marks addressable.
 *
 * @param tokens - The candidate phrase
 * @param context - The parse context
 * @param recencyView - Most-recent-first refs, for `tell her to …`
 * @returns The addressee, or `undefined`
 */
const addresseeNamedBy = (tokens: readonly Token[], context: ParseContext, recencyView: readonly EntityRef[]): Addressee | undefined => {
	const addressable = context.entities.filter((entity) => entity.isAddressable === true);
	const canonical = canonicalizeTokens(tokens, context.lexicon);
	const phrase = canonical.filter((token): token is PhraseToken => token.kind === 'word' || token.kind === 'pronoun');

	if (addressable.length === 0 || phrase.length === 0 || phrase.length !== canonical.length) {
		return undefined;
	}

	const outcome = resolveEntityPhrase(phrase, addressable, recencyView, 'none', context.claimedNames);

	return outcome.type === 'resolved' ? { entityRef: outcome.resolution.entityRef, surface: outcome.resolution.headSurface } : undefined;
};

/**
 * Split one segment into a body and its clause — led (`tell X to …`) first, then named (`X …`).
 *
 * @param tokens - The segment's tokens
 * @param context - The parse context
 * @returns The split, or `undefined` when the segment has neither shape
 */
const splitOrder = (tokens: readonly Token[], context: ParseContext): OrderSplit | undefined => {
	const [leader, ...afterLeader] = tokens;
	const toIndex = afterLeader.findIndex((token) => token.text === ORDER_PREPOSITION);

	if (leader !== undefined && context.lexicon.orderLeaderSet.has(leader.text) && toIndex >= 1 && toIndex < afterLeader.length - 1) {
		return { addresseeTokens: afterLeader.slice(0, toIndex), isLed: true, orderedTokens: afterLeader.slice(toIndex + 1) };
	}

	const verbIndex = tokens.findIndex((token, index) => index > 0 && context.lexicon.isClauseLeadingToken(token.text));

	return verbIndex < 1 ? undefined : { addresseeTokens: tokens.slice(0, verbIndex), isLed: false, orderedTokens: tokens.slice(verbIndex) };
};

/**
 * The ordered clause's result, said to the addressee: a command carries it, and is named by the whole order's text.
 *
 * @param result - The ordered clause's walk
 * @param addressee - Who it was said to
 * @param clauseText - The whole order, as the player typed it
 * @returns The result, addressed
 */
const addressedTo = (result: ClauseParseResult, addressee: Addressee, clauseText: string): ClauseParseResult =>
	result.type === 'command'
		? { command: { ...result.command, addresseeRef: addressee.entityRef, addresseeSurface: addressee.surface, clauseText }, type: 'command' }
		: result;

const segmentOf = (tokens: readonly Token[]): ClauseSegment => ({ clauseText: tokens.map((token) => token.text).join(' '), tokens });

/**
 * Read a segment — and, for a called order, the one after it — as an order to somebody.
 *
 * @param clauseSegment - The segment the walk is on
 * @param nextSegment - The one after it, if any
 * @param context - The parse context
 * @param recencyView - Most-recent-first refs for this clause
 * @param parseClause - The orchestrator's clause walk
 * @returns The reading, or `undefined` when this is not an order
 */
export const readOrder = (
	clauseSegment: ClauseSegment,
	nextSegment: ClauseSegment | undefined,
	context: ParseContext,
	recencyView: readonly EntityRef[],
	parseClause: ParseOrderedClause,
): OrderReading | undefined => {
	const split = splitOrder(clauseSegment.tokens, context);
	const addressee = split === undefined ? undefined : addresseeNamedBy(split.addresseeTokens, context, recencyView);

	if (split !== undefined && addressee !== undefined) {
		const result = addressedTo(parseClause(segmentOf(split.orderedTokens)), addressee, clauseSegment.clauseText);

		if (split.isLed || result.type === 'command') {
			return { result, segmentsRead: 1 };
		}
	}

	const [nextLeader] = nextSegment?.tokens ?? [];
	const calledAddressee =
		nextSegment === undefined || nextLeader === undefined || !context.lexicon.isClauseLeadingToken(nextLeader.text)
			? undefined
			: addresseeNamedBy(clauseSegment.tokens, context, recencyView);

	if (nextSegment === undefined || calledAddressee === undefined) {
		return undefined;
	}

	const result = addressedTo(parseClause(nextSegment), calledAddressee, `${clauseSegment.clauseText}, ${nextSegment.clauseText}`);

	return result.type === 'command' ? { result, segmentsRead: 2 } : undefined;
};
